import { Helmet } from "react-helmet-async";
import { absoluteUrl } from "@/lib/social";

interface SocialMetaProps {
  title: string;
  description?: string | null;
  path: string;
  image?: string | null;
  type?: "article" | "website" | "event";
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  publishedTime?: string | null;
  section?: string | null;
  tags?: string[] | null;
  imageWidth?: number;
  imageHeight?: number;
}

/**
 * Per-route Open Graph + Twitter Card + JSON-LD wrapper.
 * Note: this mutates head client-side, so JS-aware crawlers (Google, X)
 * pick it up; non-JS crawlers (LinkedIn, Slack, Facebook) still use the
 * sitewide tags in index.html as fallback.
 */
export default function SocialMeta({
  title,
  description,
  path,
  image,
  type = "article",
  jsonLd,
  publishedTime,
  section,
  tags,
  imageWidth,
  imageHeight,
}: SocialMetaProps) {
  const url = absoluteUrl(path);
  const safeDesc = (description ?? "").slice(0, 300);
  const jsonLdArr = jsonLd
    ? Array.isArray(jsonLd)
      ? jsonLd
      : [jsonLd]
    : [];

  return (
    <Helmet>
      <title>{title}</title>
      {safeDesc && <meta name="description" content={safeDesc} />}
      <link rel="canonical" href={url} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={title} />
      {safeDesc && <meta property="og:description" content={safeDesc} />}
      <meta property="og:url" content={url} />
      {image && <meta property="og:image" content={image} />}
      {image && imageWidth && <meta property="og:image:width" content={String(imageWidth)} />}
      {image && imageHeight && <meta property="og:image:height" content={String(imageHeight)} />}
      <meta name="twitter:card" content={image ? "summary_large_image" : "summary"} />
      <meta name="twitter:title" content={title} />
      {safeDesc && <meta name="twitter:description" content={safeDesc} />}
      {image && <meta name="twitter:image" content={image} />}
      {type === "article" && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {type === "article" && section && (
        <meta property="article:section" content={section} />
      )}
      {type === "article" && tags?.map((tag) => (
        <meta key={tag} property="article:tag" content={tag} />
      ))}
      {jsonLdArr.map((obj, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(obj)}
        </script>
      ))}
    </Helmet>
  );
}
